<template>
    <div class="legislation-chart">
        <h4 class="mb-1 chart-title">Legislación Aplicable:</h4>
        <p class="text-muted mb-2">Decreto 1496 de 2018 | Resolución 773 de 2021</p>
        <div class="card">
            <div class="card-body">
                <div class="chart-container">
                    <div ref="chart" style="height: 320px; width: 100%;"></div>
                </div>
                <div class="mt-0">
                    <div class="status-item success-item">
                        <div class="status-header">
                            <div class="dot bg-success"></div>
                            <span class="status-text">
                                Cumplen: {{ chartData.characterized }} PQ 
                            </span>
                            <i class="fa fa-info-circle text-muted" aria-hidden="true"></i>
                        </div>
                        <div class="fds-progress mt-3">
                            <div class="fds-label" translate="no">FDS review:</div>

                            <div class="progress">
                                <div class="progress-bar bg-fds" :style="reviewedStyle">
                                    {{ (reviewedPercentage == 0 ? '' : reviewedPercentage) + (reviewedPercentage == 0 ?
                                        '' : '%') }}
                                </div>
                                <div class="progress-bar bg-success1" :style="notReviewedStyle">
                                    {{ (notReviewedPercentage == 0 ? '' : notReviewedPercentage) +
                                        (notReviewedPercentage == 0 ? '' : '%') }}
                                </div>
                            </div>
                        </div>
                        <div class="status-details">
                            <div class="detail-item">
                                <div class="dot bg-success"></div>
                                <span>Con revisión menor a 5 años: <span class="status-text">{{ chartData.fdsWithReviewed }} PQ</span></span>
                            </div>
                            <div class="detail-item">
                                <div class="dot bg-success1"></div>
                                <span>Por revisar última versión del fabricante: <span class="status-text">{{ chartData.fdsNotReviewed }} PQ</span></span>
                            </div>
                        </div>
                    </div>

                    <div class="status-item warning-item">
                        <div class="status-header">
                            <div class="dot bg-warning-1"></div>
                            <span class="status-text">
                                No cumple: {{ chartData.nonCompliant }} PQ ({{ chartData.nonCompliantPercentage }}%)
                            </span>
                            <i class="fa fa-info-circle text-muted" aria-hidden="true"></i>
                        </div>
                    </div>

                    <div class="status-item danger-item">
                        <div class="status-header">
                            <div class="dot bg-danger-1"></div>
                            <span class="status-text">
                                Sin documento: {{ chartData.noDocument }} PQ ({{ chartData.noDocumentPercentage }}%)
                            </span>
                            <i class="fa fa-info-circle text-muted" aria-hidden="true"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
    name: 'LegislationChart',
    props: {
        data: {
            type: Object,
            required: true,
            default: () => ({
                characterized: 0,
                nonCompliant: 0,
                noDocument: 0,
                fdsWithReviewed: 0,
                fdsNotReviewed: 0,
                notCaracterizedWithCumply: 0,
            })
        }
    },
    data() {
        return {
            chart: null
        }
    },
    computed: {
        chartData() {
            const total = this.data.characterized + this.data.notCaracterizedWithCumply
            return {
                characterized: this.data.characterized,
                nonCompliant: this.data.nonCompliant,
                noDocument: this.data.noDocument,
                characterizedPercentage: ((this.data.characterized / total) * 100).toFixed(2),
                nonCompliantPercentage: ((this.data.nonCompliant / total) * 100).toFixed(2),
                noDocumentPercentage: ((this.data.noDocument / total) * 100).toFixed(2),
                fdsWithReviewed: this.data.fdsWithReviewed,
                fdsNotReviewed: this.data.fdsNotReviewed
            }
        },
        chartOption() {
            return {
                tooltip: {
                    trigger: 'item',
                    textStyle: {
                        fontFamily: 'Poppins'
                    }
                },
                legend: {
                    show: false
                },
                series: [
                    {
                        type: 'pie',
                        radius: ['40%', '80%'],
                        avoidLabelOverlap: true,
                        startAngle: 90,
                        minAngle: 1,
                        label: {
                            show: true,
                            position: 'outside',
                            formatter: '{d}%',
                            padding: [2, 2],
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderWidth: 1,
                            borderRadius: 4,
                            fontSize: 14,
                            fontWeight: 'bold',
                            rich: {
                                value: {
                                    fontSize: 14,
                                    fontWeight: 'bold',
                                }
                            },
                            bleedMargin: 10, // Aumentado para permitir que las etiquetas salgan del contenedor
                            minMargin: 10 // Margen mínimo entre etiquetas
                        },
                        labelLayout: {
                            draggable: false, // Desactiva el arrastre de etiquetas
                            hideOverlap: false // No oculta etiquetas solapadas
                        },
                        labelLine: {
                            show: true,
                            lineStyle: {
                                width: 2,
                                type: 'solid'
                            }
                        },
                        itemStyle: {
                            borderRadius: 4,
                            borderColor: '#fff',
                            borderWidth: 2
                        },
                        zlevel: 1,
                        clockwise: true,
                        animation: true,
                        animationDuration: 1000,
                        emphasis: {
                            scale: false,
                            scaleSize: 10,
                            focus: 'none'
                        },
                        data: [
                            {
                                value: this.chartData.characterized,
                                name: 'Comply',
                                itemStyle: { color: '#35C895' },
                                label: {
                                    show: true,
                                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                                    borderColor: '#35C895'
                                }
                            },
                            {
                                value: this.chartData.nonCompliant,
                                name: 'Do not comply',
                                itemStyle: { color: '#F27833' },
                                label: {
                                    show: true,
                                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                    borderColor: '#F27833'
                                }
                            },
                            {
                                value: this.chartData.noDocument,
                                name: 'No document',
                                itemStyle: { color: '#F24633' },
                                label: {
                                    show: true,
                                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                                    borderColor: '#F24633'
                                }
                            }
                        ].sort((a, b) => b.value - a.value)
                    }
                ]
            }
        },
        totalFds() {
            return  this.data.characterized + this.data.notCaracterizedWithCumply
        },
        reviewedPercentage() {
            return this.totalFds > 0
                ? ((this.chartData.fdsWithReviewed / (this.chartData.fdsWithReviewed + this.chartData.fdsNotReviewed)) * 100).toFixed(0)
                : 0;
        },
        notReviewedPercentage() {
            return this.totalFds > 0
                ? ((this.chartData.fdsNotReviewed / (this.chartData.fdsWithReviewed + this.chartData.fdsNotReviewed)) * 100).toFixed(0)
                : 0;
        },
        reviewedStyle() {
            return { width: this.reviewedPercentage + '%' };
        },
        notReviewedStyle() {
            return { width: this.notReviewedPercentage + '%' };
        }
    },
    mounted() {
        this.initChart()
        window.addEventListener('resize', this.handleResize)
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.dispose()
        }
        window.removeEventListener('resize', this.handleResize)
    },
    watch: {
        data: {
            deep: true,
            handler() {
                this.updateChart()
            }
        }
    },
    methods: {
        initChart() {
            if (this.chart) {
                this.chart.dispose()
            }
            this.chart = echarts.init(this.$refs.chart, null, {
                renderer: 'svg'
            })
            this.updateChart()
        },
        updateChart() {
            if (this.chart) {
                this.chart.setOption(this.chartOption)
            }
        },
        handleResize() {
            if (this.chart) {
                this.chart.resize()
            }
        }
    }
}
</script>

<style scoped>
.legislation-chart {
    font-family: 'Poppins', sans-serif;
}

.chart-container {
    height: auto;
    width: 100%;
    position: relative;
    margin: 0 auto;
    max-width: 800px;
}

.card {
    border: 1px solid #e2e8f0;
    border-radius: 25px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
    background-color: #fff;
}

.card-body {
    padding: 1rem;
}

h4 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 0.5rem;
}

.text-muted {
    color: #6c757d;
    font-size: 0.9rem;
}

.status-item {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 12px;
}

.success-item {
    background-color: rgba(40, 167, 69, 0.1);
}

.warning-item {
    background-color: #F279331F;
}

.danger-item {
    background-color: #E9443225;
}

.status-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.status-text {
    font-weight: 500;
    color: #1a1a1a;
    flex-grow: 1;
}

.dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex-shrink: 0;
}

.dot-small {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.fds-progress {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin: 0.75rem 0;
}

.fds-label {
    font-weight: 500;
    color: #495057;
    min-width: 45px;
}

.progress {
    flex-grow: 1;
    height: 24px;
    background-color: rgba(233, 236, 239, 0.5);
    border-radius: 6px;
    overflow: hidden;
}

.progress-bar {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 500;
    color: white;
    transition: width 0.3s ease;
}

.bg-fds {
    background-color: #35C895;
}

.status-details {
    margin-top: 1rem;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    color: #495057;
}

.bg-success {
    background-color: #35C895;
}

.bg-success1 {
    background-color: #22956D;
}

.bg-warning-1 {
    background-color: #F27833;
}

.bg-danger-1 {
    background-color: #E94432;
}

.fa-info-circle {
    font-size: 14px;
    color: #6c757d;
    cursor: help;
}

.chart-title {
    position: relative;
    margin-bottom: 0;
    font-size: 18px;
    font-weight: 800;
    color: #1B212E;
    z-index: 1;
}

/* Media queries para asegurar la visibilidad en pantallas pequeñas */
@media (max-width: 768px) {
    .chart-container {
        height: 400px;
    }

    .card-body {
        padding: 1rem;
    }

    /* Ajustes específicos para el gráfico en móviles */
    :deep(.echarts-container) {
        margin: 0 -1rem;
        /* Compensar el padding del card-body */
    }
}
</style>
